import { UserInputError } from "./errors-CEDWDXew.js";
import { isNumericId, validateSlugOrId } from "./slug-validation-MJ3jU0Hd.js";
import { ParamOrganizationSlug, ParamPeriod, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { compactLines, formatActor, formatDate, formatId, formatUnknown } from "./api-formatting-nIGXUFdx.js";
import { assertProjectRefWithinConstraint } from "./project-constraints-DlfcU4mm.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-monitor-details.ts
function formatProject(monitor) {
	if (!monitor.project) return null;
	return monitor.project.slug ?? monitor.project.name ?? null;
}
function formatConfig(monitor) {
	const config = monitor.config;
	if (!config) return [];
	return [
		"schedule",
		"schedule_type",
		"timezone",
		"checkin_margin",
		"max_runtime"
	].filter((key) => config[key] !== void 0).map((key) => `- **${key}**: ${formatUnknown(config[key])}`);
}
function formatCheckIn(checkIn) {
	const date = formatDate(checkIn.dateCreated) ?? formatDate(checkIn.dateUpdated) ?? "unknown time";
	const duration = checkIn.duration === void 0 || checkIn.duration === null ? "" : `, ${checkIn.duration}s`;
	const environment = checkIn.environment ? `, ${checkIn.environment}` : "";
	return `- ${date}: ${checkIn.status ?? "unknown"}${duration}${environment}`;
}
function formatStat(stat) {
	const timestamp = formatDate((/* @__PURE__ */ new Date(stat.ts * 1e3)).toISOString()) ?? String(stat.ts);
	const parts = Object.entries(stat).filter(([key]) => key !== "ts").map(([key, value]) => `${key}=${formatUnknown(value)}`);
	return `- ${timestamp}: ${parts.join(", ") || "no check-ins"}`;
}
var get_monitor_details_default = defineTool({
	name: "get_monitor_details",
	skills: ["inspect"],
	requiredScopes: ["project:read"],
	description: [
		"Get details for a Sentry cron monitor.",
		"",
		"Use this tool when you need to:",
		"- Inspect a monitor's schedule, status, owner, project, and environments",
		"- Review recent check-ins for missed, failed, timeout, or OK runs",
		"- Check monitor stats over a recent time range",
		"",
		"<examples>",
		"get_monitor_details(organizationSlug='my-organization', monitorSlug='nightly-import')",
		"get_monitor_details(organizationSlug='my-organization', monitorSlug='nightly-import', projectSlugOrId='backend', environment='production', period='7d')",
		"</examples>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		regionUrl: ParamRegionUrl.nullable().default(null),
		projectSlugOrId: z.string().trim().superRefine(validateSlugOrId).nullable().default(null).describe("Optional project slug or numeric ID. Use this to disambiguate monitors with the same slug or to scope a monitor URL that includes a project segment."),
		monitorSlug: z.string().trim().min(1).describe("Monitor slug or GUID."),
		environment: z.string().trim().describe("Optional environment name to filter monitor environments, recent check-ins, and stats.").nullable().default(null),
		period: ParamPeriod.describe("Relative time range. Defaults to `24h` when `start` and `end` are omitted.").nullable().default(null),
		start: z.string().datetime().describe("Absolute start time. Must be provided with `end`; do not combine with `period`.").nullable().default(null),
		end: z.string().datetime().describe("Absolute end time. Must be provided with `start`; do not combine with `period`.").nullable().default(null),
		checkInLimit: z.number().int().positive().max(50).describe("Maximum number of recent check-ins to include.").default(10),
		includeStats: z.boolean().describe("Include aggregate check-in stats for the selected time range.").default(true),
		rollupSeconds: z.number().int().positive().describe("Optional stats bucket size in seconds. Omit this to let Sentry choose an appropriate rollup.").nullable().default(null)
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		setTag("monitor.slug", params.monitorSlug);
		const requestedProjectSlugOrId = params.projectSlugOrId ?? void 0;
		const projectSlugOrId = requestedProjectSlugOrId ?? context.constraints.projectSlug ?? null;
		if (projectSlugOrId) if (isNumericId(projectSlugOrId)) setTag("project.id", projectSlugOrId);
		else setTag("project.slug", projectSlugOrId);
		if (requestedProjectSlugOrId && !isNumericId(requestedProjectSlugOrId)) assertProjectRefWithinConstraint({
			resourceLabel: "Monitor",
			scopedProjectSlug: context.constraints.projectSlug,
			project: { slug: requestedProjectSlugOrId }
		});
		if (requestedProjectSlugOrId && isNumericId(requestedProjectSlugOrId) && context.constraints.projectSlug) {
			const scopedProject = await apiService.getProject({
				organizationSlug,
				projectSlugOrId: context.constraints.projectSlug
			});
			if (String(scopedProject.id) !== requestedProjectSlugOrId) throw new UserInputError(`Monitor is outside the active project constraint. Expected project "${context.constraints.projectSlug}".`);
		}
		const start = params.start ?? void 0;
		const end = params.end ?? void 0;
		if (start && !end || !start && end) throw new UserInputError("`start` and `end` must be provided together.");
		const hasAbsoluteTimeRange = start !== void 0 || end !== void 0;
		const requestedPeriod = params.period?.trim() || void 0;
		if (hasAbsoluteTimeRange && requestedPeriod) throw new UserInputError("`period` cannot be combined with `start` and `end`.");
		const statsPeriod = hasAbsoluteTimeRange ? void 0 : requestedPeriod ?? "24h";
		const monitor = await apiService.getMonitorDetails({
			organizationSlug,
			projectSlug: projectSlugOrId ?? void 0,
			monitorSlug: params.monitorSlug,
			environment: params.environment ?? void 0
		});
		assertProjectRefWithinConstraint({
			resourceLabel: "Monitor",
			scopedProjectSlug: context.constraints.projectSlug,
			project: monitor.project
		});
		const project = formatProject(monitor);
		const monitorUrl = apiService.getMonitorUrl(organizationSlug, monitor.slug, project ?? void 0);
		const [checkIns, stats] = await Promise.all([apiService.listMonitorCheckIns({
			organizationSlug,
			projectSlug: projectSlugOrId ?? void 0,
			monitorSlug: params.monitorSlug,
			environment: params.environment ?? void 0,
			statsPeriod,
			start,
			end,
			limit: params.checkInLimit
		}), params.includeStats ? apiService.getMonitorStats({
			organizationSlug,
			projectSlug: projectSlugOrId ?? void 0,
			monitorSlug: params.monitorSlug,
			environment: params.environment ?? void 0,
			statsPeriod,
			start,
			end,
			rollup: params.rollupSeconds ?? void 0
		}) : Promise.resolve([])]);
		const owner = monitor.owner ? formatActor(monitor.owner) : null;
		const output = compactLines([
			`# Monitor ${monitor.name ?? monitor.slug} in **${organizationSlug}**`,
			"",
			`**Slug**: ${monitor.slug}`,
			`**ID**: ${formatId(monitor.id)}`,
			project ? `**Project**: ${project}` : null,
			monitor.status ? `**Status**: ${monitor.status}` : null,
			monitor.type ? `**Type**: ${monitor.type}` : null,
			owner ? `**Owner**: ${owner}` : null,
			formatDate(monitor.lastCheckIn) ? `**Last Check-In**: ${formatDate(monitor.lastCheckIn)}` : null,
			formatDate(monitor.nextCheckIn) ? `**Next Check-In**: ${formatDate(monitor.nextCheckIn)}` : null,
			`**URL**: [Open Monitor](${monitorUrl})`
		]);
		const config = formatConfig(monitor);
		if (config.length > 0) output.push("", "## Schedule", "", ...config);
		if (monitor.environments && monitor.environments.length > 0) {
			output.push("", "## Environments", "");
			for (const environment of monitor.environments) output.push(compactLines([
				`- ${environment.name ?? "unknown"}`,
				environment.status ? `  - Status: ${environment.status}` : null,
				formatDate(environment.lastCheckIn) ? `  - Last check-in: ${formatDate(environment.lastCheckIn)}` : null,
				formatDate(environment.nextCheckIn) ? `  - Next check-in: ${formatDate(environment.nextCheckIn)}` : null
			]).join("\n"));
		}
		output.push("", "## Recent Check-Ins", "");
		output.push(checkIns.length === 0 ? "No check-ins found in this time range." : checkIns.slice(0, params.checkInLimit).map(formatCheckIn).join("\n"));
		if (params.includeStats) {
			output.push("", "## Stats", "");
			output.push(stats.length === 0 ? "No stats found in this time range." : stats.slice(-10).map(formatStat).join("\n"));
		}
		output.push("", "## Response Notes", "");
		output.push(`- Search issues from this monitor with \`search_issues\` query \`monitor.slug:${monitor.slug}\`.`);
		return `${output.join("\n")}\n`;
	}
});

//#endregion
export { get_monitor_details_default };
//# sourceMappingURL=get-monitor-details-CP_Cj6P3.js.map